if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
            .catch((error) => {
              if (error.name === 'AbortError') {
                console.warn('Fetch aborted by user');
              } else {
                console.error(error);
                this.pendingRequestUrl = null;
                this.productForm?.handleErrorMessage(window.variantStrings.selectionError, true);
              }
            });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          this.variantSelectors.replaceWith(variantSelects);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        const nextUrl = new URL(window.location.href);
        nextUrl.pathname = new URL(url, window.location.href).pathname;
        nextUrl.searchParams.delete('option_values');
        if (variantId) nextUrl.searchParams.set('variant', variantId);
        else nextUrl.searchParams.delete('variant');
        window.history.replaceState({}, '', nextUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

(() => {
  return;
  const productPath = window.location.pathname;
  if (!productPath.includes('/products/beer-of-satoshi')) return;
  const isBosZero =
    productPath.includes('beer-of-satoshi-zero') || productPath.includes('2027-alcohol-free');

  function addBosProductStyle() {
    if (document.querySelector('[data-bos-product-enhance-style="true"]')) return;
    document.head.insertAdjacentHTML(
      'beforeend',
      '<style data-bos-product-enhance-style="true">.bos-product-summary{background:rgba(var(--color-foreground),.035);border:.1rem solid rgba(var(--color-foreground),.16);border-radius:.8rem;margin:1rem 0 0;padding:1.4rem}.bos-product-summary strong{display:block;font-size:1.45rem;line-height:1.25}.bos-product-summary p{margin:.6rem 0 0}.bos-info-table{border-collapse:collapse;margin:1.2rem 0;width:100%}.bos-info-table th,.bos-info-table td{border:.1rem solid rgba(var(--color-foreground),.18);padding:1rem;text-align:left;vertical-align:top}.bos-info-table th{background:rgba(var(--color-foreground),.055);font-weight:700}.bos-info-note{color:rgba(var(--color-foreground),.75);font-size:1.35rem;margin-top:.8rem}.bos-product-faq details{border-top:.1rem solid rgba(var(--color-foreground),.18);padding:1.4rem 0}.bos-product-faq summary{cursor:pointer;font-weight:700}@media(max-width:749px){.bos-info-table{display:block;overflow-x:auto}}</style>'
    );
  }

  function addProductSummary() {
    if (document.querySelector('.bos-product-summary')) return;
    const title = document.querySelector('.product__title');
    if (!title) return;
    const html = isBosZero
      ? '<div class="bos-product-summary"><strong>Alcohol-free &middot; Q1 2027 pre-order &middot; hidden sats reward</strong><p>Beer of Satoshi Zero is the upcoming alcohol-free release. Payment is collected now; planned dispatch is Q1 2027.</p></div>'
      : '<div class="bos-product-summary"><strong>4.9% vol &middot; ships now &middot; hidden sats reward</strong><p>Genesis Lager is the original Beer of Satoshi release. Choose a 6, 12, or 24 pack, then scan the hidden cap reward after opening.</p></div>';
    title.insertAdjacentHTML('afterend', html);
  }

  function addProductTabs() {
    const tabs = document.querySelector('.w3-bar');
    const description = document.getElementById('description');
    if (!tabs || !description) return;

    if (!document.getElementById('compare')) {
      const firstButton = tabs.querySelector('button');
      if (firstButton) {
        firstButton.insertAdjacentHTML(
          'afterend',
          '<button class="w3-bar-item w3-button tablink" onclick="openTab(event,\'compare\')">Compare</button>'
        );
      }
      description.insertAdjacentHTML(
        'afterend',
        '<div id="compare" class="w3-container w3-border city" style="display:none"><h2>Genesis vs Beer of Satoshi Zero</h2><table class="bos-info-table"><thead><tr><th>Detail</th><th>Genesis Lager</th><th>Beer of Satoshi Zero</th></tr></thead><tbody><tr><th scope="row">Release</th><td>Original Beer of Satoshi release</td><td>New alcohol-free release</td></tr><tr><th scope="row">Alcohol</th><td>4.9% vol</td><td>Alcohol-free</td></tr><tr><th scope="row">Availability</th><td>Ships from available stock</td><td>Q1 2027 pre-order</td></tr><tr><th scope="row">Pack sizes</th><td>6, 12, or 24 bottles</td><td>6, 12, or 24 bottles</td></tr><tr><th scope="row">Reward</th><td>Hidden cap reward, claim with Lightning</td><td>Hidden cap reward, claim with Lightning</td></tr><tr><th scope="row">Best for</th><td>Original lager, events, gifts, and collectors</td><td>Alcohol-free Bitcoin gifts and early Beer of Satoshi Zero access</td></tr><tr><th scope="row">Price</th><td>From EUR 4.92/bottle plus EUR 0.08 deposit in the 24-pack</td><td>Pre-order from EUR 2.87/bottle plus EUR 0.08 deposit in the 24-pack</td></tr></tbody></table></div>'
      );
    }

    const shipping = document.getElementById('shipping');
    if (shipping && !shipping.querySelector('.bos-info-table')) {
      const internationalShippingRow = '<tr><th scope="row">International</th><td>Carrier services, duties and taxes are shown at checkout where available.</td></tr>';
      shipping.insertAdjacentHTML(
        'afterbegin',
        `<h2>Shipping estimate</h2><table class="bos-info-table"><tbody><tr><th scope="row">Germany</th><td>DHL Paket is shown at checkout for supported pack sizes.</td></tr>${internationalShippingRow}<tr><th scope="row">Pack sizes</th><td>6, 12, and 24 packs are supported. The checkout shows the final shipping method, price, and delivery estimate.</td></tr><tr><th scope="row">Pre-order</th><td>Beer of Satoshi Zero ships in Q1 2027. Checkout is the final source of truth for available services and price.</td></tr></tbody></table><p class="bos-info-note">Exact shipping availability, price, and delivery estimate are confirmed at checkout.</p>`
      );
    }

    if (!document.getElementById('faq')) {
      tabs.insertAdjacentHTML(
        'beforeend',
        '<button class="w3-bar-item w3-button tablink" onclick="openTab(event,\'faq\')">FAQ</button>'
      );
      const faqShippingCopy = isBosZero
        ? 'Beer of Satoshi Zero is an alcohol-free pre-order. Shipping is planned for Q1 2027. The pre-order price starts from EUR 2.87/bottle plus EUR 0.08 deposit in the 24-pack.'
        : 'Genesis Lager ships from available stock. Delivery options and dates are shown at checkout.';
      const faqDifferenceCopy = isBosZero
        ? 'Beer of Satoshi Zero is the new alcohol-free beer release and is currently available as a Q1 2027 pre-order. The exact beer style will be confirmed before fulfillment.'
        : 'Genesis Lager is the original 4.9% vol release. Beer of Satoshi Zero is the alcohol-free pre-order.';
      document.querySelector('.w3-wrapper').insertAdjacentHTML(
        'beforeend',
        `<div id="faq" class="w3-container w3-border city bos-product-faq" style="display:none"><h2>FAQ</h2><details open><summary>When does this ship?</summary><p>${faqShippingCopy}</p></details><details><summary>How do I claim the Bitcoin reward?</summary><p>Open the bottle, scan the QR code under the cap with your phone camera, then claim the sats with a Lightning wallet.</p></details><details><summary>Which pack sizes are available?</summary><p>You can choose 6, 12, or 24 bottles. The 24-pack has the best price per bottle.</p></details><details><summary>How is shipping calculated?</summary><p>Checkout calculates available carriers, shipping, duties and taxes from your destination and selected pack size.</p></details><details><summary>Can I pick up at an event?</summary><p>Event pickup is not currently offered.</p></details><details><summary>What is the difference between Genesis and Beer of Satoshi Zero?</summary><p>${faqDifferenceCopy}</p></details></div>`
      );
    }
  }

  function addHowtoVideo() {
    if (document.querySelector('[data-bos-youtube-howto="true"]')) return;
    const description = document.getElementById('description');
    if (!description) return;

    description.insertAdjacentHTML(
      'beforeend',
      '<div class="bos-howto-video bos-howto-video--visible" data-bos-youtube-howto="true"><h3>How it works</h3><p><a href="https://www.youtube.com/watch?v=eKi45INtSJw" target="_blank" rel="noopener noreferrer">Watch the claim walkthrough on YouTube ↗</a></p></div>'
    );
  }

  function enhanceBosProductPage() {
    addBosProductStyle();
    addProductSummary();
    addProductTabs();
    addHowtoVideo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceBosProductPage);
  } else {
    enhanceBosProductPage();
  }
})();
